import type { Position, Size } from './canvasMath';

/**
 * Selection rectangle math for the Zoom op. The user drags a rect on top
 * of the image; we capture start/end points and normalize them into an
 * axis-aligned rect in image-space (so the rect remains stable under
 * pan/zoom).
 *
 * The shape we emit matches what the SSGP v3 Zoom request would want
 * (centerX / centerY / zoomFactor) but normalized to image-space pixels —
 * the panel converts to whatever the BE contract expects.
 */

export interface ImageRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Build a normalized rect from two corner points. Handles dragging in any
 * direction (right-to-left, bottom-to-top) by always returning a positive
 * width/height with the top-left at (min, min).
 */
export function rectFromPoints(start: Position, end: Position): ImageRect {
  const x = Math.min(start.x, end.x);
  const y = Math.min(start.y, end.y);
  return {
    x,
    y,
    width: Math.abs(end.x - start.x),
    height: Math.abs(end.y - start.y),
  };
}

/**
 * Clamp a rect into the bounds of an image of `size`. Drags often spill off
 * the canvas edges; clamping keeps the emitted rect inside the source.
 *
 * If the clamped rect would have zero area (the drag started outside the
 * image and never re-entered, or the image dims are non-positive), returns
 * null — the caller treats null as "no selection".
 */
export function clampRectToImage(rect: ImageRect, size: Size): ImageRect | null {
  if (size.width <= 0 || size.height <= 0) return null;
  const x = Math.max(0, Math.min(rect.x, size.width));
  const y = Math.max(0, Math.min(rect.y, size.height));
  const right = Math.max(0, Math.min(rect.x + rect.width, size.width));
  const bottom = Math.max(0, Math.min(rect.y + rect.height, size.height));
  const width = right - x;
  const height = bottom - y;
  if (width <= 0 || height <= 0) return null;
  return { x, y, width, height };
}

/**
 * A drag is "meaningful" (worth emitting as a selection) when it covers at
 * least `minPx` in both width and height. Below the threshold the drag is
 * treated as a click — caller typically resets the selection state.
 */
export function isMeaningfulSelection(rect: ImageRect, minPx = 4): boolean {
  return rect.width >= minPx && rect.height >= minPx;
}

/**
 * Convert a normalized rect into the Zoom-style centerX/centerY/zoomFactor
 * shape the BE Zoom request expects. The center is the midpoint of the
 * rect in [0..1] coords relative to the source image; the zoom factor is
 * the ratio of source-image-size to rect-size on the larger axis.
 */
export interface ZoomParams {
  centerX: number;
  centerY: number;
  zoomFactor: number;
}

export function rectToZoomParams(rect: ImageRect, size: Size): ZoomParams | null {
  if (size.width <= 0 || size.height <= 0) return null;
  if (rect.width <= 0 || rect.height <= 0) return null;
  const centerX = (rect.x + rect.width / 2) / size.width;
  const centerY = (rect.y + rect.height / 2) / size.height;
  // Pick the larger axis ratio so the entire rect fits in the zoomed view.
  const xRatio = size.width / rect.width;
  const yRatio = size.height / rect.height;
  const zoomFactor = Math.min(xRatio, yRatio);
  return { centerX, centerY, zoomFactor };
}
